import React, { useEffect, useRef, useState } from "react";
import { Send, Sparkles, Loader2, MessageSquare } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { API } from "@/lib/api";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

export default function ChatDrawer({ comparedIds }) {
  const { t, lang } = useApp();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionId = useRef("sess-" + Math.random().toString(36).slice(2));
  const scrollRef = useRef();

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId.current, message: text,
          compared_ids: comparedIds || [], lang,
        }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "⚠️ " + t("extractError") };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <button
          data-testid="open-chat"
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 bg-primary px-5 py-3.5 font-semibold text-primary-foreground shadow-lg hover:bg-primary/90"
        >
          <Sparkles className="h-4 w-4" />
          {t("chatTitle")}
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="flex w-full flex-col gap-0 border-border bg-background p-0 sm:max-w-md">
        <SheetHeader className="border-b border-border px-5 py-4 text-left">
          <SheetTitle className="flex items-center gap-2 font-display tracking-tight">
            <MessageSquare className="h-4 w-4 text-primary" /> {t("chatTitle")}
          </SheetTitle>
          <p className="text-xs text-muted-foreground">{t("chatHint")}</p>
        </SheetHeader>

        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-5" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="border border-border bg-surface p-4 text-sm text-muted-foreground">
              {t("chatIntro")}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[85%] whitespace-pre-wrap px-3.5 py-2.5 text-sm ${
                  m.role === "user"
                    ? "bg-primary text-primary-foreground"
                    : "border border-border bg-card"
                }`}
              >
                {m.content || (streaming && i === messages.length - 1 ? (
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("thinking")}
                  </span>
                ) : "")}
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-border p-3">
          <div className="flex items-end gap-2">
            <textarea
              data-testid="chat-input"
              rows={2}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={t("chatPlaceholder")}
              className="flex-1 resize-none border border-input bg-surface px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary"
            />
            <Button
              data-testid="chat-send"
              onClick={send}
              disabled={streaming || !input.trim()}
              className="h-full rounded-none bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
